import { Alert, Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormLabel, IconButton, InputAdornment, Paper, Stack, TextField, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams, GridActionsCellItem, GridRowId, DataGrid } from "@mui/x-data-grid";
import { Timestamp } from "firebase/firestore";
import { useEffect, useState } from "react";
import { DeleteUser, GetAllManagedUsers, UpdateUser } from "../FirebaseQueries";
import BulkCreateStudentsDialog from "../components/BulkCreateStudentsDialog";
import DeleteIcon from '@mui/icons-material/Delete';
import { RenderPassword } from "../components/CellRenderers/Password";
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import SearchIcon from '@mui/icons-material/Search';
import { red } from "@mui/material/colors";
import CreateTeacherUserDialog from "../components/CreateTeacherUserDialog";
import { useAuth } from "../AuthProvider";

interface User {
	id: string;
	email: string;
	fullName: string;
	password: string;
	role: string;
	createdAt: Timestamp;
}

interface Result {
	id: string,
	message: string,
	success: boolean
}

export default function UsersManaged() {
	const { currentRole } = useAuth();
	const [users, setUsers] = useState<User[]>([]);
	const [search, setSearch] = useState<string>("");
	const [isBulkCreating, setIsBulkCreating] = useState<boolean>(false);
	const [isCreatingTeacher, setIsCreatingTeacher] = useState<boolean>(false);

	const [idToDelete, setIdToDelete] = useState<string>("");
	const [nameToDelete, setNameToDelete] = useState<string>("");
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
	const [isDeleting, setIsDeleting] = useState<boolean>(false);

	const [userToEdit, setUserToEdit] = useState<User | undefined>(undefined);
	const [userToEditPlaceholder, setUserToEditPlaceholder] = useState<User | undefined>(undefined);
	const [confirmPassword, setConfirmPassword] = useState<string>("");
	const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [showEditPassword, setShowEditPassword] = useState<boolean>(false);
	const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
	const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");

	const [responseCollapseText, setResponseCollapseText] = useState<string>("");
	const redColour: string = red[600];

	const columns: GridColDef[] = [
		{
			field: "fullName",
			headerName: "Name",
			flex: 1,
			renderCell: (params: GridRenderCellParams<any, string>) => (
				<a href={`/app/users/${params.id}`}>
					{params.value}
				</a>
			),
		},
		{ field: "email", headerName: "E-mail", flex: 1 },
		{
			field: "password",
			type: "string",
			headerName: "Password",
			flex: 1,
			renderCell: RenderPassword
		},
		{
			field: "role", headerName: "Role", flex: 1, valueFormatter: (value: string) => {
				if (!value) return '';
				return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
			}
		},
		{ field: "createdAt", headerName: "Created At", flex: 1, valueFormatter: (value: Timestamp) => FormatTimestamp(value) },
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			width: 100,
			cellClassName: "actions",
			getActions: ({ id }) => {
				return [
					<GridActionsCellItem icon={<EditIcon />} label="Edit" onClick={OpenEditUserDialog(id)} />,
					<GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={OpenDeleteUserDialog(id)} />
				];
			},
		},
	];

	// ---------- useEffect ----------

	useEffect(() => {
		const GetManagedUsers = async () => {
			try {
				const usersSnapshot = await GetAllManagedUsers();
				let value: User[] = [];

				usersSnapshot.forEach((doc) => {
					value.push({
						id: doc.id,
						email: doc.data().email,
						fullName: doc.data().fullName,
						password: doc.data().password,
						role: doc.data().role,
						createdAt: doc.data().createdAt
					})
				});

				value.sort((a, b) => b.createdAt.seconds - a.createdAt.seconds);
				setUsers(value);
			} catch (err) {
				console.log(err);
			}
		};

		GetManagedUsers();
	}, []);

	// ---------- Values ----------

	function FilteredUsers() {
		let returnValue: User[] = users;

		if (search != "") {
			returnValue = returnValue.filter((user: User) => {
				if (user.email !== undefined) {
					if (user.email.toLowerCase().includes(search.toLowerCase())) {
						return true;
					}
				}

				if (user.fullName !== undefined) {
					if (user.fullName.toLowerCase().includes(search.toLowerCase())) {
						return true;
					}
				}

				return false;
			});
		}

		return returnValue;
	}

	function FormatTimestamp(ts: Timestamp): string {
		if (ts === undefined) return "N/A";

		let date: Date = ts.toDate();
		const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

		return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
	}

	function HasEditError() {
		return passwordErrors.length > 0 || confirmPasswordError !== ""
	}

	// ---------- Functions ----------

	function HandleTeacherCreateDialogOpen() {
		setIsCreatingTeacher(true);
	}

	function HandleTeacherCreateDialogClose() {
		setIsCreatingTeacher(false);

	}

	function HandleBulkCreateDialogOpen() {
		setIsBulkCreating(true);
	}

	function HandleBulkCreateDialogClose() {
		setIsBulkCreating(false);
	}

	const OpenEditUserDialog = (id: GridRowId) => () => {
		const selectedUser = users.find((user) => user.id === id);
		if (selectedUser !== undefined) {
			setUserToEdit({ ...selectedUser });
			setUserToEditPlaceholder({ ...selectedUser });
			setConfirmPassword(selectedUser.password);
			setPasswordErrors([]);
			setConfirmPasswordError("");
			setShowEditPassword(false);
			setIsEditing(false);
			setIsEditDialogOpen(true);
		}
	}

	const OpenDeleteUserDialog = (id: GridRowId) => () => {
		const selectedUser = users.find((user) => user.id === id);
		if (selectedUser !== undefined) {
			setIdToDelete(selectedUser.id);
			setNameToDelete(`${selectedUser.fullName} (${selectedUser.email})`);
			setIsDeleting(false);
			setIsDeleteDialogOpen(true);
		}
	};

	async function HandleDeleteUser() {
		if (idToDelete === "") return;

		try {
			setIsDeleting(true);
			const res: Result = await DeleteUser(idToDelete);
			setUsers([...users.filter((value) => value.id !== idToDelete)]);
			setResponseCollapseText(res.message);
			setIsDeleteDialogOpen(false);
		} catch (err) {
			console.log(err);
			setResponseCollapseText(String(err));
		}
	}

	async function HandleEditUser() {
		if (userToEdit === undefined) return;

		try {
			setIsEditing(true);
			const res: Result = await UpdateUser(userToEdit.id, userToEdit.email, userToEdit.fullName, userToEdit.password);
			if (res.success) {
				setUsers((prev) => {
					let copy = [...prev];
					let editedIndex = copy.findIndex((user) => user.id === userToEdit.id);
					if (editedIndex >= 0) copy[editedIndex] = userToEdit;
					return [...copy];
				})
			}
			setResponseCollapseText(res.message);
			setIsEditDialogOpen(false);
		} catch (err) {
			setResponseCollapseText(String(err));
		}
	}

	function HandleEditUserEmail(event: any) {
		const value: string = event.target.value;
		if (userToEdit !== undefined) {
			userToEdit.email = value;
			setUserToEdit({ ...userToEdit });
		}
	}

	function HandleEditUserName(event: any) {
		const value: string = event.target.value;
		if (userToEdit !== undefined) {
			userToEdit.fullName = value;
			setUserToEdit({ ...userToEdit });
		}
	}

	function HandleEditUserPassword(event: any) {
		const value: string = event.target.value;
		if (userToEdit !== undefined) {
			userToEdit.password = value;
			setUserToEdit({ ...userToEdit });

			const hasDigit = /\d/.test(value);
			const hasLowercase = /[abcdefghijklmnopqrstuvwxyz]/.test(value);
			const hasUppercase = /[ABCDEFGHIJKLMNOPQRSTUVWXYZ]/.test(value);
			const isMinimumLength = value.length >= 6;

			let errors = []
			if (!isMinimumLength) errors.push("Must be at least 6 characters.");
			if (!hasLowercase) errors.push("Must include at least 1 lower case character.");
			if (!hasUppercase) errors.push("Must include at least 1 upper case character.");
			if (!hasDigit) errors.push("Must include at least 1 digit.");

			setConfirmPasswordError(value === confirmPassword ? "" : "Passwords must match.");
			setPasswordErrors(errors);
		}
	}

	function HandleEditUserConfirmPassword(event: any) {
		const value: string = event.target.value;
		setConfirmPassword(value);

		if (userToEdit !== undefined) {
			if (value !== userToEdit.password) {
				setConfirmPasswordError("Passwords must match.");
			}
			else {
				setConfirmPasswordError("");
			}
		}
	}

	function HandleSearch(event: any) {
		setSearch(event.target.value);
	}

	function HandleDownloadCSV() {
		let headers: string = "Name,Email,Password\n";
		let rows: string = "";

		FilteredUsers().forEach((user: User) => {
			rows += `${user.fullName},${user.email},${user.password}\n`;
		});

		let csvContent = "data:text/csv;charset=utf-8," + headers + rows;
		var encodedUri = encodeURI(csvContent);
		window.open(encodedUri);
	}

	const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};

	const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};

	return (
		<>
			{/* ---------- Dialogs ---------- */}
			<BulkCreateStudentsDialog isOpen={isBulkCreating} onClose={HandleBulkCreateDialogClose} />
			<CreateTeacherUserDialog isOpen={isCreatingTeacher} onClose={HandleTeacherCreateDialogClose} />
			{/* ---------- Delete Dialog ---------- */}
			<Dialog open={isDeleteDialogOpen}>
				<DialogTitle>Delete {nameToDelete}?</DialogTitle>
				<DialogContent sx={{ minWidth: 500 }}>
					<Typography>Are you sure? This cannot be undone.</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsDeleteDialogOpen(false)}>Close</Button>
					<Button onClick={HandleDeleteUser} loading={isDeleting} variant="contained" color="error">Confirm Delete</Button>
				</DialogActions>
			</Dialog>
			{/* ---------- Edit Dialog ---------- */}
			<Dialog open={isEditDialogOpen}>
				<DialogTitle>Editing {userToEdit?.fullName}</DialogTitle>
				<DialogContent sx={{ minWidth: 500 }}>
					<Stack gap={2}>
						<FormControl>
							<FormLabel htmlFor="name">Name</FormLabel>
							<TextField
								id="name"
								value={userToEdit?.fullName}
								placeholder={userToEditPlaceholder?.fullName}
								variant="outlined"
								onChange={HandleEditUserName}
							>
							</TextField>
						</FormControl>
						<FormControl>
							<FormLabel htmlFor="email">Email</FormLabel>
							<TextField
								id="email"
								value={userToEdit?.email}
								placeholder={userToEditPlaceholder?.email}
								variant="outlined"
								onChange={HandleEditUserEmail}
							>
							</TextField>
						</FormControl>
						<FormControl>
							<FormLabel htmlFor="password">Password</FormLabel>
							<TextField
								id="password"
								value={userToEdit?.password}
								placeholder={showEditPassword ? userToEditPlaceholder?.password : "******"}
								variant="outlined"
								onChange={HandleEditUserPassword}
								type={showEditPassword ? "text" : "password"}
								slotProps={{
									input: {
										endAdornment:
											<InputAdornment position="end">
												<IconButton
													aria-label={
														showEditPassword ? 'hide the password' : 'display the password'
													}
													onClick={() => setShowEditPassword(!showEditPassword)}
													onMouseDown={handleMouseDownPassword}
													onMouseUp={handleMouseUpPassword}
													edge="end"
												>
													{showEditPassword ? <Visibility /> : <VisibilityOff />}
												</IconButton>
											</InputAdornment>
									}
								}}
							>
							</TextField>
						</FormControl>
						<FormControl>
							<FormLabel htmlFor="confirmPassword">Confirm Password</FormLabel>
							<TextField
								id="password"
								value={confirmPassword}
								placeholder={"******"}
								variant="outlined"
								onChange={HandleEditUserConfirmPassword}
								type={showEditPassword ? "text" : "password"}
							>
							</TextField>
							<Stack minHeight={22}>
								<Typography color={redColour}>{confirmPasswordError}</Typography>
								{passwordErrors.map((error, index) => (
									<Typography key={index} color={redColour}>{error}</Typography>
								))}
							</Stack>
						</FormControl>
					</Stack>
				</DialogContent >
				<DialogActions>
					<Button onClick={() => setIsEditDialogOpen(false)}>Close</Button>
					<Button onClick={HandleEditUser} loading={isEditing} disabled={HasEditError()} variant="contained">Confirm Edit</Button>
				</DialogActions>
			</Dialog>
			{/* ---------- Content ---------- */}
			<Stack spacing={2}>
				{/* ---------- Collapse ---------- */}
				<Collapse in={responseCollapseText !== ""} mountOnEnter unmountOnExit >
					<Alert icon={<InfoIcon fontSize="inherit" />} severity="error"
						action={
							<IconButton
								aria-label="close"
								color="inherit"
								size="small"
								onClick={() => {
									setResponseCollapseText("");
								}}>
								<CloseIcon fontSize="inherit" />
							</IconButton>
						}>
						<Typography>{responseCollapseText}</Typography>
					</Alert>
				</Collapse >
				{/* ---------- Top Bar ---------- */}
				<Box sx={{ display: "flex", justifyContent: "right", gap: 1 }}>
					<TextField
						onChange={HandleSearch}
						value={search}
						placeholder="Search"
						variant="outlined"
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon />
									</InputAdornment>
								),
							},
						}}
					/>
					{currentRole === "admin" ?
						<Button onClick={HandleTeacherCreateDialogOpen} variant="contained">
							<Typography>Create Teacher User</Typography>
						</Button>
						:
						<></>
					}
					<Button onClick={HandleBulkCreateDialogOpen} variant="contained">
						<Typography>Bulk Create Student Users</Typography>
					</Button>
					<Button onClick={HandleDownloadCSV} variant="contained">
						<Typography>Download CSV</Typography>
					</Button>
				</Box >
				{/* ---------- Main ---------- */}
				<Paper variant="outlined">
					<DataGrid
						pageSizeOptions={[15, 50]}
						sx={{ p: 1 }}
						rows={FilteredUsers()}
						columns={columns}
						initialState={{
							pagination: { paginationModel: { pageSize: 15 } },
						}}
					/>
				</Paper>
			</Stack >
		</>
	);
}